
    // IMPORTS
    import { computed, defineComponent } from 'vue'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'

    import { TreeView, TreeViewExpandChangeEvent, TreeViewItemClickEvent, processTreeViewItems } from '@progress/kendo-vue-treeview'

    import { buildLocalization } from '@/util'
    import { PropertyValueDTO } from '@/store/classification'

    interface ISparepartTypeNode {
        id: string,
        text: string,
        items: ISparepartTypeNode[],
        resultCount: number | null
    }

    export default defineComponent({
        name: 'sparepartTypeTree',

        components: {
            TreeView,
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparepartFilterStore = useSparePartFilterStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = { }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const treeState = computed(() => sparepartFilterStore.typeTreeState)
            const sparepartTypeStats = computed(() => sparepartFilterStore.sparePartTypeStats)
            const allSparepartTypePropertyValues = computed(() => sparepartFilterStore.allSparepartTypePropertyValues)

            const sparepartTypeNodes = computed(() => getTreeNodes(allSparepartTypePropertyValues.value))

            const treeDataItems = computed(() => processTreeViewItems(sparepartTypeNodes.value,
                {
                    expand: treeState.value.expanded,
                    select: treeState.value.selected
                })
            )

            const computedData = { treeDataItems }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function onExpandChange(event: TreeViewExpandChangeEvent) {
                let ids = treeState.value.expanded.ids.slice()
                const index = ids.indexOf(event.item.id)
                index === -1
                    ? ids.push(event.item.id)
                    : ids.splice(index, 1)

                treeState.value.expanded.ids = ids
            }

            function onSelect(event: TreeViewItemClickEvent) {
                const selectedItem = event.item as ISparepartTypeNode

                // Update state
                if (selectedItem != null) {
                    sparepartFilterStore.setSelectedType(selectedItem.id)
                }
            }

            const publicMethods = { localize, onExpandChange, onSelect }

            function getTreeNodes(typePropertyValues: PropertyValueDTO[]): ISparepartTypeNode[] {
                const root = {
                    propertyValue: 'all',
                    propertyValueText: localize('all')                    
                } as PropertyValueDTO

                return [buildNode(null, root, typePropertyValues) ]
            }

            function buildNode(parent: string | null,
                pv: PropertyValueDTO,
                typePropertyValues: PropertyValueDTO[]): ISparepartTypeNode
            {
                const childItems = getNodes(parent, typePropertyValues)

                const resultCount: number | null = childItems.length > 0
                    ? childItems.reduce<number | null>((acc, item) => (item.resultCount == null || acc == null)
                        ? null
                        : acc + item.resultCount, 0)
                    : getResultCount(pv.propertyValue)

                return {
                    id: pv.propertyValue,
                    text: resultCount == null
                        ? pv.propertyValueText
                        : `${pv.propertyValueText} [${resultCount}]`,
                    items: childItems,
                    resultCount: resultCount
                }
            }

            function getResultCount(propertyValue: string): number | null {
                if (sparepartTypeStats.value.size == 0) {
                    return null
                }

                return sparepartTypeStats.value.get(propertyValue) ?? 0
            }

            function getNodes(parent: string | null, typePropertyValues: PropertyValueDTO[]): ISparepartTypeNode[] {
                return typePropertyValues
                    .filter(pv => pv.parentPropertyValue == parent)
                    .sortBy(pv => pv.propertyValueText)
                    .map(pv => buildNode(pv.propertyValue, pv, typePropertyValues))
                    .filter(n => n.resultCount == null || n.resultCount > 0)
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
